import {
  followResource,
  FollowResourceData,
  FollowResourceResponse,
  unfollowResource,
  UnfollowResourceData,
  UnfollowResourceResponse,
  updateFollowers,
  UpdateFollowersData,
  UpdateFollowersResponse,
  getResourceFollowers,
  GetResourceFollowersResponse,
  checkFollowStatusBulk,
  CheckFollowStatusBulkData,
  CheckFollowStatusBulkResponse,
} from '@data-access/connect/tc-follow';
import type { Client } from '@data-access/connect/tc-issue-management';
import {
  useQuery,
  useMutation,
  UseQueryResult,
  UseMutationResult,
  QueryClient,
} from '@tanstack/solid-query';
import { Accessor } from 'solid-js';

// Constants for cache times (in milliseconds)
const FOLLOWERS_STALE_TIME = 120000; // 2 minutes
const FOLLOW_STATUS_STALE_TIME = 30000; // 30 seconds

// Batch size limits for follow status bulk API based on environment
const FOLLOW_BULK_BATCH_SIZE_INT_QA = 10; // INT and QA support max 10 items
const FOLLOW_BULK_BATCH_SIZE_DEFAULT = 100; // Stage and Prod support max 100 items

type Environment = 'int' | 'qa' | 'stage' | 'prod';

export class FollowApiService {
  constructor(
    private readonly client: Client,
    private readonly queryClient: QueryClient,
    private readonly getEnvironment?: () => Environment
  ) {}

  /**
   * Get the maximum batch size for follow status bulk API based on environment
   */
  private getBulkBatchSize(): number {
    const env = this.getEnvironment?.();
    // INT and QA have smaller batch size limit
    if (env === 'int' || env === 'qa') {
      return FOLLOW_BULK_BATCH_SIZE_INT_QA;
    }
    // Stage and Prod support larger batches
    return FOLLOW_BULK_BATCH_SIZE_DEFAULT;
  }

  // Query for getting resource followers
  public getResourceFollowersQuery(
    contextId: Accessor<string | undefined>,
    resourceId: Accessor<string | undefined>,
    options?: { pageSize?: number; skipToken?: string }
  ): UseQueryResult<GetResourceFollowersResponse, Error> {
    return useQuery(() => ({
      queryKey: ['follows', 'followers', contextId(), resourceId(), options],
      queryFn: async (): Promise<GetResourceFollowersResponse> => {
        const context = contextId();
        const resource = resourceId();
        if (!context || !resource) {
          throw new Error('Context ID and Resource ID are required');
        }

        const response = await getResourceFollowers({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          path: { contextId: context },
          query: {
            resourceId: resource,
            pageSize: options?.pageSize,
            skipToken: options?.skipToken,
          },
        });

        if (response.error) {
          throw new Error('Failed to get resource followers');
        }

        return response.data;
      },
      enabled: !!contextId() && !!resourceId(),
      staleTime: FOLLOWERS_STALE_TIME, // 2 minutes - follower data doesn't change very frequently
    }));
  }

  // Mutation for following a resource
  public followResourceMutation(): UseMutationResult<
    FollowResourceResponse,
    Error,
    FollowResourceData
  > {
    return useMutation(() => ({
      mutationFn: async (
        data: FollowResourceData
      ): Promise<FollowResourceResponse> => {
        const response = await followResource({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to follow resource');
        }

        return response.data;
      },
      onSuccess: (_, variables) => {
        // Invalidate followers queries for the context
        void this.queryClient.invalidateQueries({
          queryKey: ['follows', 'followers', variables.path.contextId],
        });
        // Invalidate any follow status queries
        void this.queryClient.invalidateQueries({
          queryKey: ['follows', 'status'],
        });
      },
    }));
  }

  // Mutation for unfollowing a resource
  public unfollowResourceMutation(): UseMutationResult<
    UnfollowResourceResponse,
    Error,
    UnfollowResourceData
  > {
    return useMutation(() => ({
      mutationFn: async (
        data: UnfollowResourceData
      ): Promise<UnfollowResourceResponse> => {
        const response = await unfollowResource({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to unfollow resource');
        }

        return response.data!;
      },
      onSuccess: (_, variables) => {
        // Invalidate followers queries for the context
        void this.queryClient.invalidateQueries({
          queryKey: ['follows', 'followers', variables.path.contextId],
        });
        // Invalidate any follow status queries
        void this.queryClient.invalidateQueries({
          queryKey: ['follows', 'status'],
        });
      },
    }));
  }

  // Mutation for updating followers list (admin operation)
  public updateFollowersMutation(): UseMutationResult<
    UpdateFollowersResponse,
    Error,
    UpdateFollowersData
  > {
    return useMutation(() => ({
      mutationFn: async (
        data: UpdateFollowersData
      ): Promise<UpdateFollowersResponse> => {
        const response = await updateFollowers({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to update followers');
        }

        return response.data;
      },
      onSuccess: (_, variables) => {
        // Invalidate followers queries for the context
        void this.queryClient.invalidateQueries({
          queryKey: ['follows', 'followers', variables.path.contextId],
        });
        // Invalidate any follow status queries
        void this.queryClient.invalidateQueries({
          queryKey: ['follows', 'status'],
        });
      },
    }));
  }

  // Mutation for checking follow status in bulk
  public checkFollowStatusBulkMutation(): UseMutationResult<
    CheckFollowStatusBulkResponse,
    Error,
    CheckFollowStatusBulkData
  > {
    return useMutation(() => ({
      mutationFn: async (
        data: CheckFollowStatusBulkData
      ): Promise<CheckFollowStatusBulkResponse> => {
        const response = await checkFollowStatusBulk({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to check follow status');
        }

        return response.data;
      },
      // Note: This is a read operation so we don't invalidate queries
    }));
  }

  /**
   * Alternative query-based approach for checking follow status (for frequent status checks)
   * Automatically batches requests based on environment limits
   */
  public checkFollowStatusQuery(
    contextId: Accessor<string | undefined>,
    checkData: Accessor<CheckFollowStatusBulkData['body'] | undefined>
  ): UseQueryResult<CheckFollowStatusBulkResponse, Error> {
    return useQuery(() => ({
      queryKey: ['follows', 'status', contextId(), checkData()],
      queryFn: async (): Promise<CheckFollowStatusBulkResponse> => {
        const context = contextId();
        const checks = checkData();
        if (!context || !checks) {
          throw new Error('Context ID and check data are required');
        }

        const resources = checks.resources || [];
        const batchSize = this.getBulkBatchSize();

        // If resources fit in one batch, make single request
        if (resources.length <= batchSize) {
          const response = await checkFollowStatusBulk({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            client: this.client as any,
            path: { contextId: context },
            body: checks,
          });

          if (response.error) {
            throw new Error('Failed to check follow status');
          }
          return response.data;
        }

        // Split resources into batches and make parallel requests
        const batches: Array<CheckFollowStatusBulkData['body']['resources']> =
          [];
        for (let i = 0; i < resources.length; i += batchSize) {
          batches.push(resources.slice(i, i + batchSize));
        }

        // Execute all batches in parallel
        const batchPromises = batches.map((batch) =>
          checkFollowStatusBulk({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            client: this.client as any,
            path: { contextId: context },
            body: { resources: batch },
          })
        );

        const responses = await Promise.all(batchPromises);

        // Check for errors in any batch
        const errorResponse = responses.find((r) => r.error);
        if (errorResponse?.error) {
          throw new Error('Failed to check follow status');
        }

        // Combine all batch responses into single response
        const combinedResources =
          responses.flatMap((r) => r.data?.resources || []) || [];

        return {
          resources: combinedResources,
        };
      },
      enabled: !!contextId() && !!checkData(),
      staleTime: FOLLOW_STATUS_STALE_TIME, // 30 seconds - status might change more frequently
    }));
  }
}
